import { LINHA_DO_DRE, type Dre, type ValorDaCategoria } from "@bookdental/schemas";

/** Uma linha da tabela: o valor já com sinal (custo negativo) e as categorias que abrem nela. */
export type LinhaDaTabela = {
  chave: string;
  rotulo: string;
  total?: boolean;
  forte?: boolean;
  valor: (d: Dre) => number;
  categorias?: (d: Dre) => ValorDaCategoria[];
  estimado?: (d: Dre) => boolean;
};

const negativas = (cs: ValorDaCategoria[]) => cs.map((c) => ({ ...c, valorCents: -c.valorCents }));
/** Custo zerado vira -0, que o formatador mostra como "-R$ 0,00". */
export const semMenosZero = (v: number) => v || 0;
const despesa = (chave: keyof Dre["despesas"], rotulo: string): LinhaDaTabela => ({
  chave,
  rotulo: `(−) ${rotulo}`,
  valor: (d) => -d.despesas[chave].valorCents,
  categorias: (d) => negativas(d.despesas[chave].categorias),
});

export const LINHAS: LinhaDaTabela[] = [
  { chave: "faturamento", rotulo: "Faturamento da produção", valor: (d) => d.faturamentoCents },
  { chave: "outrasReceitas", rotulo: "Outras receitas", valor: (d) => d.outrasReceitas.valorCents, categorias: (d) => d.outrasReceitas.categorias },
  { chave: "receitaBruta", rotulo: "Receita bruta", total: true, valor: (d) => d.receitaBrutaCents },
  { chave: "descontos", rotulo: `(−) ${LINHA_DO_DRE.DESCONTO.rotulo}`, valor: (d) => -d.descontos.valorCents, categorias: (d) => negativas(d.descontos.categorias) },
  {
    chave: "deducoes",
    rotulo: `(−) ${LINHA_DO_DRE.DEDUCAO.rotulo}`,
    valor: (d) => -d.deducoes.valorCents,
    categorias: (d) => negativas(d.deducoes.categorias),
    estimado: (d) => d.deducoes.estimado,
  },
  { chave: "receitaLiquida", rotulo: "Receita líquida", total: true, valor: (d) => d.receitaLiquidaCents },
  { chave: "custo", rotulo: `(−) ${LINHA_DO_DRE.CUSTO_DO_SERVICO.rotulo}`, valor: (d) => -d.custoDosServicos.valorCents, categorias: (d) => negativas(d.custoDosServicos.categorias) },
  { chave: "lucroBruto", rotulo: "Lucro bruto", total: true, valor: (d) => d.lucroBrutoCents },
  despesa("PESSOAL", LINHA_DO_DRE.PESSOAL.rotulo),
  despesa("OCUPACAO", LINHA_DO_DRE.OCUPACAO.rotulo),
  despesa("ADMINISTRATIVA", LINHA_DO_DRE.ADMINISTRATIVA.rotulo),
  despesa("COMERCIAL", LINHA_DO_DRE.COMERCIAL.rotulo),
  despesa("PERDAS_COM_CLIENTES", LINHA_DO_DRE.PERDAS_COM_CLIENTES.rotulo),
  despesa("OUTRAS", "Outras despesas (sem linha do DRE)"),
  { chave: "resultadoOperacional", rotulo: "Resultado operacional (EBITDA)", total: true, valor: (d) => d.resultadoOperacionalCents },
  { chave: "depreciacao", rotulo: "(−) Depreciação", valor: (d) => -d.depreciacao.valorCents, categorias: (d) => negativas(d.depreciacao.categorias) },
  {
    chave: "financeiro",
    rotulo: `(±) ${LINHA_DO_DRE.RESULTADO_FINANCEIRO.rotulo}`,
    valor: (d) => d.financeiro.valorCents,
    categorias: (d) => [...d.financeiro.entradas.categorias, ...negativas(d.financeiro.saidas.categorias)],
  },
  {
    chave: "ir",
    rotulo: `(−) ${LINHA_DO_DRE.IMPOSTO_SOBRE_O_LUCRO.rotulo}`,
    valor: (d) => -d.impostoSobreOLucro.valorCents,
    categorias: (d) => negativas(d.impostoSobreOLucro.categorias),
    estimado: (d) => d.impostoSobreOLucro.estimado,
  },
  { chave: "resultado", rotulo: "Resultado do mês", total: true, forte: true, valor: (d) => d.resultadoCents },
];

/** Linha que não é subtotal e está zerada em todos os meses mostrados some da tabela. */
export const linhasVisiveis = (dres: Dre[]) =>
  LINHAS.map((l) => ({ ...l, valor: (d: Dre) => semMenosZero(l.valor(d)) })).filter((l) => l.total || dres.some((d) => l.valor(d) !== 0));

/** As categorias de uma linha em todos os meses, na ordem do maior valor somado. */
export function categoriasDaLinha(l: LinhaDaTabela, dres: Dre[]) {
  if (!l.categorias) return [];
  const mapa = new Map<string, { id: string; nome: string; cor: string | null; total: number }>();
  for (const d of dres) for (const c of l.categorias(d)) {
    const atual = mapa.get(c.id) ?? { id: c.id, nome: c.nome, cor: c.cor, total: 0 };
    atual.total += Math.abs(c.valorCents);
    mapa.set(c.id, atual);
  }
  return [...mapa.values()].sort((a, b) => b.total - a.total);
}

export const valorDaCategoria = (l: LinhaDaTabela, d: Dre, id: string) => semMenosZero(l.categorias?.(d).find((c) => c.id === id)?.valorCents ?? 0);

export const percentual = (valor: number, d: Dre) =>
  d.receitaBrutaCents ? `${((valor / d.receitaBrutaCents) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}%` : "—";

/** Soma de vários meses no formato de um DRE só, para a coluna Total do modo Ano. */
export function somarDres(dres: Dre[]): Dre {
  const somaCats = (listas: ValorDaCategoria[][]) => {
    const m = new Map<string, ValorDaCategoria>();
    for (const c of listas.flat()) {
      const a = m.get(c.id);
      if (a) a.valorCents += c.valorCents;
      else m.set(c.id, { ...c });
    }
    return [...m.values()];
  };
  const linha = (f: (d: Dre) => { valorCents: number; categorias: ValorDaCategoria[] }) => ({
    valorCents: dres.reduce((t, d) => t + f(d).valorCents, 0),
    categorias: somaCats(dres.map((d) => f(d).categorias)),
  });
  const n = (f: (d: Dre) => number) => dres.reduce((t, d) => t + f(d), 0);
  const despesas = Object.fromEntries(Object.keys(dres[0]!.despesas).map((k) => [k, linha((d) => d.despesas[k as keyof Dre["despesas"]])])) as Dre["despesas"];
  return {
    faturamentoCents: n((d) => d.faturamentoCents),
    outrasReceitas: linha((d) => d.outrasReceitas),
    receitaBrutaCents: n((d) => d.receitaBrutaCents),
    descontos: linha((d) => d.descontos),
    deducoes: { ...linha((d) => d.deducoes), estimado: dres.some((d) => d.deducoes.estimado) },
    receitaLiquidaCents: n((d) => d.receitaLiquidaCents),
    custoDosServicos: linha((d) => d.custoDosServicos),
    lucroBrutoCents: n((d) => d.lucroBrutoCents),
    despesas,
    despesasOperacionaisCents: n((d) => d.despesasOperacionaisCents),
    resultadoOperacionalCents: n((d) => d.resultadoOperacionalCents),
    depreciacao: linha((d) => d.depreciacao),
    financeiro: { entradas: linha((d) => d.financeiro.entradas), saidas: linha((d) => d.financeiro.saidas), valorCents: n((d) => d.financeiro.valorCents) },
    impostoSobreOLucro: { ...linha((d) => d.impostoSobreOLucro), estimado: dres.some((d) => d.impostoSobreOLucro.estimado), estimadoCents: n((d) => d.impostoSobreOLucro.estimadoCents) },
    provisaoCents: n((d) => d.provisaoCents),
    resultadoCents: n((d) => d.resultadoCents),
    foraDoResultado: { entradas: linha((d) => d.foraDoResultado.entradas), saidas: linha((d) => d.foraDoResultado.saidas) },
    margem: {
      custoDaProducaoCents: n((d) => d.margem.custoDaProducaoCents),
      outrosVariaveisCents: n((d) => d.margem.outrosVariaveisCents),
      fixosCents: n((d) => d.margem.fixosCents),
    },
  };
}
